import type { ComputedRef } from 'vue'

type StartChat = (options: {
  chatId: string | null
  canResumeLatest?: boolean
  withPageContext?: boolean
}) => Promise<void>

/**
 * Orchestrates a "seeded launch": opening the assistant into a brand-new chat
 * pre-supplied with page context and/or an initial message, typically from an
 * external entry point (push notification, email, in-page CTA).
 *
 * This composable owns the sequence and the decisions; the side-effect
 * primitives (`startChat`, `sendSeededMessage`) are injected and owned by the
 * page, so the composable stays unit-testable in isolation.
 */
export function useSeededLaunch({
  isSeededLaunch,
  seededMessage,
  startChat,
  sendSeededMessage,
}: {
  isSeededLaunch: ComputedRef<boolean>
  seededMessage: ComputedRef<string>
  startChat: StartChat
  sendSeededMessage: (content: string) => Promise<void>
}) {
  /**
   * Runs the seeded launch when the route carries seeded params. Returns `true`
   * when it handled the launch (so the caller skips the normal resume path),
   * `false` otherwise.
   */
  async function maybeLaunch(): Promise<boolean> {
    if (!isSeededLaunch.value) {
      return false
    }

    // Force-new: never resume the latest chat. Page context is attached when present.
    await startChat({
      chatId: null,
      canResumeLatest: false,
      withPageContext: true,
    })

    const message = seededMessage.value
    if (message) {
      await sendSeededMessage(message)
    }

    return true
  }

  return {
    maybeLaunch,
  }
}
